<template>
  <div class="container" :class="{ 'margin-dock-active': marginDockActive }">
    <div class="scroll-progress" v-show="showScrollProgress && !routeProgressPending && !previewMode && !isRoomRoute" :style="{ transform: 'scaleX(' + scrollPct + ')' }"></div>
    <header class="header home-canvas-layout" :class="{ 'home-direct-nav': directHomeNavHidden, 'home-route': isHomeRoute && !$route.meta.monochromeHome, 'home-material-ready': mobileHomeHeaderReady, 'home-video-cursor': homeVideoCursor, 'intro-root': isIntroRoot, 'initial-chrome-pending': !initialChromeReady, 'home-arrival-pending': homeArrivalPending, 'mobile-menu-surface': mobileMenuOpen || mobileMenuClosing }" v-show="!cvChromeHidden && !previewMode && !isRoomRoute">
      <h3 class="header-title desktop-global-nav">
        <router-link to="/home" custom v-slot="{ href }">
          <a :href="href" aria-label="Henry Wang, home" @pointerdown="beginHomeIdentityPress" @pointercancel="cancelHomeIdentityPress" @pointerleave="cancelHomeIdentityPress" @click.prevent="markTabNav(); navigateHomeWithPress($event)"><IdentityMark /></a>
        </router-link>
        <small class="header-subtitle">Explore. Understand. Build.</small>
        <div class="menu">
          <nav class="menu-content" :class="'nav-tabs-' + navTabsLayout">
            <router-link to="/home" class="menu-item home" @click="markHomeNav">Home</router-link>
            <router-link to="/education" class="menu-item education" @click="markTabNav">Study</router-link>
            <router-link to="/experience" class="menu-item experience" @click="markTabNav">Work</router-link>
            <router-link to="/research" class="menu-item research" @click="markTabNav">Research</router-link>
            <router-link to="/projects" class="menu-item projects" @click="markTabNav">Builds</router-link>
            <span class="menu-item menu-view-group">
              <router-link to="/fragments" class="fragments" @click="markTabNav">Fragments</router-link>
              <span class="menu-view-separator" aria-hidden="true"></span>
              <router-link to="/views" class="menu-view-link" @click="markTabNav"><AiViewTerm plural /></router-link>
            </span>
          </nav>
          <nav class="social-icons" aria-label="Site controls">
            <button class="site-control search-trigger" type="button" title="Ask or search (⌘K)" aria-label="Ask or search" @click="openSearch"><i class="fa fa-search" aria-hidden="true"></i></button>
            <button id="toggle-theme" data-theme-control class="site-control" type="button" title="Toggle Theme" aria-label="Toggle theme"><i class="fa fa-moon" aria-hidden="true"></i></button>
            <button id="music-icon" data-music-control class="site-control" type="button" aria-label="Play music"><i class="fa fa-compact-disc" aria-hidden="true"></i></button>
          </nav>
        </div>
        <audio id="background-music" preload="none"></audio>
      </h3>
      <div class="mobile-global-nav" :class="{ 'is-open': mobileMenuOpen || mobileMenuClosing }">
        <router-link
          to="/home"
          class="mobile-global-identity"
          aria-label="Home"
          @click="markHomeNav"
        ><IdentityMark /></router-link>
        <div class="mobile-global-actions" aria-label="Site controls">
          <button class="mobile-global-control" type="button" aria-label="Ask or search" @click="openSearchFromMobile">
            <i class="fa fa-search" aria-hidden="true"></i>
          </button>
          <button data-theme-control class="mobile-global-control" type="button" :aria-label="dockThemeLabel" @click="toggleThemeFromDock">
            <i :class="dockThemeIconClass" aria-hidden="true"></i>
          </button>
          <button data-music-control class="mobile-global-control" type="button" :aria-label="dockMusicLabel" @click="toggleMusicFromDock">
            <i :class="dockMusicIconClass" aria-hidden="true"></i>
          </button>
        </div>
        <button
          ref="mobileMenuToggle"
          class="mobile-menu-toggle"
          :class="{ 'is-open': mobileMenuOpen || mobileMenuClosing }"
          type="button"
          :aria-expanded="String(mobileMenuOpen)"
          aria-controls="mobile-site-menu"
          :aria-label="mobileMenuOpen || mobileMenuClosing ? 'Close menu' : 'Open menu'"
          @click="toggleMobileMenu"
        >
          <MobileMenuGlyph class="mobile-menu-icon" :open="mobileMenuOpen" />
        </button>
        <teleport to="body">
            <div
              v-if="mobileMenuOpen || mobileMenuClosing"
              id="mobile-site-menu"
              ref="mobileMenuPanel"
              class="mobile-menu-panel"
              :class="{ 'is-closing': mobileMenuClosing }"
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
              tabindex="-1"
            >
              <nav ref="mobileMenuNav" class="mobile-menu-links">
                <router-link
                  v-for="item in mobileMenuItems"
                  :key="item.to"
                  :to="item.to"
                  custom
                  v-slot="{ href, isActive }"
                >
                  <a
                    :href="href"
                    :class="{
                      'router-link-active': item.to === '/home' ? isHomeRoute : isActive,
                      'mobile-menu-view': item.view,
                    }"
                    :style="{
                      '--mobile-menu-index': item.index,
                      '--mobile-menu-reverse-index': mobileMenuItems.length - item.index - 1,
                    }"
                    @click.prevent="navigateFromMobileMenu(item.to)"
                  >
                    <AiViewTerm v-if="item.view" plural />
                    <template v-else>{{ item.label }}</template>
                  </a>
                </router-link>
              </nav>
            </div>
        </teleport>
      </div>
    </header>
    <transition name="site-back-fade">
      <p v-if="siteBackTarget && (!isHomeRoute || siteBackAskReturn) && !previewMode && !isCv && !viewReturnTarget && !isRoomRoute" class="back-link back-link-top site-back-global">
        <button type="button" class="site-back-control" @click="returnToSiteOrigin">
          <span class="back-link-arrow" aria-hidden="true">←</span>
          <span>Back to {{ siteBackLabel }}</span>
        </button>
      </p>
    </transition>
    <aside
      class="margin-dock-left"
      v-show="marginDockActive && !previewMode && !isRoomRoute"
      aria-label="Site identity and current page"
      @mouseenter="holdDockPin"
      @mouseleave="releaseDockPin"
    >
      <router-link to="/" custom v-slot="{ href }">
        <a :href="href" class="margin-dock-home" aria-label="Henry Wang, home" @pointerdown="beginHomeIdentityPress" @pointercancel="cancelHomeIdentityPress" @pointerleave="cancelHomeIdentityPress" @click.prevent="markTabNav(); navigateHomeWithPress($event, { introduction: true })">
          <IdentityMark class="margin-dock-identity" />
          <small class="margin-dock-subtitle">Explore. Understand. Build.</small>
        </a>
      </router-link>
      <FittedMarginTitle ref="marginDockTitle" :title="dockPage.title" :detail="dockPage.isDetail" />
    </aside>
    <nav
      class="margin-dock-controls"
      v-show="marginDockActive && !previewMode && !isRoomRoute"
      aria-label="Persistent site controls"
      @mouseenter="holdDockPin"
      @mouseleave="releaseDockPin"
    >
      <div class="margin-dock-control-zone margin-dock-theme-zone">
        <button data-theme-control class="site-control margin-dock-control margin-dock-theme" type="button" :aria-label="dockThemeLabel" @pointerup="releaseDockPointerFocus" @click="toggleThemeFromDock">
          <i :class="dockThemeIconClass" aria-hidden="true"></i>
        </button>
      </div>
      <div class="margin-dock-control-zone margin-dock-search-zone">
        <button class="site-control margin-dock-control margin-dock-search" type="button" aria-label="Ask or search" @pointerup="releaseDockPointerFocus" @click="openSearch">
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
      <div class="margin-dock-control-zone margin-dock-music-zone">
        <button data-music-control class="site-control margin-dock-control margin-dock-music" type="button" :aria-label="dockMusicLabel" @pointerup="releaseDockPointerFocus" @click="toggleMusicFromDock">
          <i :class="dockMusicIconClass" aria-hidden="true"></i>
        </button>
      </div>
    </nav>
    <MusicControls />
    <div class="content-container" :class="{ 'cv-route': isCv, 'view-source-return-active': !!viewReturnTarget }">
      <transition name="view-return">
        <div v-if="viewReturnTarget && !previewMode" class="view-return-slot">
          <button
            type="button"
            class="view-return-control"
            aria-label="Back to the View you came from"
            @click="returnToSourceView"
          >
            <span class="view-return-arrow" aria-hidden="true">←</span>
            <span>Back to View</span>
          </button>
        </div>
      </transition>
      <router-view v-slot="{ Component }">
        <transition :name="routeTransitionName" mode="out-in" @before-enter="beforeRouteEnter" @after-enter="finishRouteProgressTransition">
          <keep-alive :include="viewOriginCache">
            <component :is="Component" @page-title="routePageTitle = $event" />
          </keep-alive>
        </transition>
      </router-view>
      <SourceViewLinks
        v-if="detailSourceId && !previewMode && !isRoomRoute"
        class="detail-source-views"
        :source-id="detailSourceId"
      />
    </div>
    <footer class="footer" :class="{ 'home-direct-nav': directHomeNavHidden, 'home-route': isArchivedHome, 'footer-signature': $route.meta.monochromeHome, 'initial-chrome-pending': !initialChromeReady }" v-show="!cvChromeHidden && !previewMode && !isRoomRoute && $route.path !== '/travels'">
      <span v-if="$route.meta.monochromeHome" class="footer-identity" role="img" aria-label="Henry Wang"><IdentityMark expanded /></span>
      <a class="footer-social" href="https://www.instagram.com/henry_w_hy/" target="_blank" aria-label="Instagram">
        <i class="fab fa-instagram" aria-hidden="true"></i>
      </a>
      <a class="footer-social" href="https://www.linkedin.com/in/henrywhy/" target="_blank" aria-label="LinkedIn">
        <i class="fab fa-linkedin" aria-hidden="true"></i>
      </a>
      <a class="footer-social" href="https://github.com/henryhyw/" target="_blank" aria-label="GitHub">
        <i class="fab fa-github" aria-hidden="true"></i>
      </a>
      <router-link class="footer-social" to="/cv" title="CV" aria-label="CV">
        <i class="far fa-file-alt" aria-hidden="true"></i>
      </router-link>
      <div class="footer-description">
        <span v-if="$route.meta.monochromeHome">Explore. Understand. Build.</span>
        <router-link v-else to="/home">H.W. | Explore. Understand. Build.</router-link>
      </div>
    </footer>
    <SearchPalette v-if="!isRoomRoute" ref="search" @ai-complete="showAiCompletion" />
    <teleport :to="viewNoticeHost || 'body'">
      <transition name="ai-ready">
        <aside
          v-if="showViewJobNotice"
          class="ai-ready-notice ai-compose-notice"
          :class="{ 'is-museum-notice': viewNoticeHost }"
        >
          <ViewComposeProgress v-if="viewNoticeHost" compact :inline="false" :started-at="activeViewJob.startedAt" />
          <button
            v-else
            class="ai-compose-notice-button"
            type="button"
            aria-label="Open View composition progress"
            @click="openActiveViewJob"
          >
            <ViewComposeProgress compact :inline="false" :started-at="activeViewJob.startedAt" />
          </button>
        </aside>
      </transition>
      <transition name="ai-ready">
        <aside
          v-if="aiCompletion"
          class="ai-ready-notice"
          :class="{ 'is-museum-notice': viewNoticeHost }"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <button class="ai-ready-main" type="button" @click="openAiCompletion">
            <span class="ai-ready-mark" aria-hidden="true">✦</span>
            <span class="ai-ready-copy">
              <strong>{{ aiCompletion.failed ? 'Your View needs another try' : (aiCompletion.kind === 'view' ? 'Your View is ready' : 'Your answer is ready') }}</strong>
              <span>{{ aiCompletion.label }}</span>
            </span>
            <span class="ai-ready-arrow" aria-hidden="true">→</span>
          </button>
          <button class="ai-ready-dismiss" type="button" aria-label="Dismiss" @click="aiCompletion = null">×</button>
        </aside>
      </transition>
    </teleport>
  </div>
</template>

<script>
import { isMuseumRoute, rememberMuseumOrigin, prefersMuseum } from './utils/viewMuseumSession';
import { revealViewTransition } from './utils/viewPresentationTransition';
import { isHomeIntroduction, requestHomeIntroduction } from './utils/homeEntrance';
import { marginDockFits } from './utils/marginDockLayout';
import { projectById } from './data/projects';
import SearchPalette from './components/SearchPalette.vue';
import { consumeAskSourceDeparture, loadHomeAskSnapshot, saveHomeAskSnapshot } from './utils/askReturn.js';
import SourceViewLinks from './components/ui/SourceViewLinks.vue';
import AiViewTerm from './components/ui/AiViewTerm.vue';
import FittedMarginTitle from './components/ui/FittedMarginTitle.vue';
import ViewComposeProgress from './components/view/ViewComposeProgress.vue';
import { viewProgressPresence } from './utils/viewProgressPresence';
import MobileMenuGlyph from './components/ui/MobileMenuGlyph.vue';
import MusicControls from './components/ui/MusicControls.vue';
import IdentityMark from './components/ui/IdentityMark.vue';
import { initCircularMediaRails } from './utils/mediaRails';
import {
  addAskTurn,
  clearSiteOrigin,
  consumeTabNavigation,
  loadAskSession,
  getSiteOrigin,
  markTabNavigation,
  prepareCollectionReturn,
  setPendingViewReturnScroll,
  setAskOrigin,
  setAskView,
  setSiteOrigin,
  setViewOriginRoute,
} from './utils/askSession';
import { candidatesForView } from './utils/viewRetrieval';
import { sanitizeViewResponse, validateViewResponse } from './utils/viewValidation';
import { clearViewJob, loadViewJob, resumeViewJob, viewComposeState } from './utils/viewSite';
import { settleScrollToY } from './utils/anchorLanding';
import { clearRouteReturn, getRouteReturn, markReturnRouteEntered, markRouteArrived, prepareRouteReturn } from './utils/routeScroll';
import { prepareHomeTabOpening } from './components/home/homeOpening';
import { animate } from 'motion';

// The dock elements only ever translate (resting transforms are pure
// translateY), so the live on-screen offset is just the matrix translation.
function currentTranslation(element) {
  try {
    const m = new DOMMatrix(getComputedStyle(element).transform);
    return { x: m.m41, y: m.m42 };
  } catch (e) {
    return { x: 0, y: 0 };
  }
}

function isGeneratedViewPath(path) {
  const value = String(path || '');
  return value === '/view' || value.startsWith('/view/');
}

function siteOriginLabel(from) {
  if (from.path === '/' || from.path === '/home') {
    const index = document.getElementById('story-index');
    if (index && index.getBoundingClientRect().top <= window.innerHeight * 0.42) return 'Index';
    return 'Home';
  }
  if (from.path === '/projects') return 'Builds';
  if (from.path === '/views') return document.querySelector('.view-museum[open]') ? 'Gallery' : 'Views';
  const meta = String((from.meta && from.meta.title) || '');
  const name = meta.replace(/\s*-\s*H\.W\.\s*$/, '').trim();
  return name || (String(from.path).split('/').filter(Boolean).pop() || 'Home');
}

export default {
  name: 'App',
  components: { FittedMarginTitle, AiViewTerm, MobileMenuGlyph, MusicControls, IdentityMark, SearchPalette, SourceViewLinks, ViewComposeProgress },
  data() {
    return {
      scrollPct: 0,
      routeProgressPending: false,
      navTabsLayout: 'one',
      cvChromeHidden: false,
      marginDockActive: false,
      routePageTitle: { path: '', title: '' },
      marginDockPinned: false,
      dockHoverCount: 0,
      dockThemeIconClass: 'fa fa-moon',
      dockThemeLabel: 'Toggle theme',
      dockMusicIconClass: 'fa fa-compact-disc',
      dockMusicLabel: 'Play music',
      directHomeNavHidden: false,
      homeArrivalPending: typeof window !== 'undefined'
        && isHomeIntroduction(window.location.hash)
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      initialChromeReady:
        typeof window === 'undefined'
        || isHomeIntroduction(window.location.hash)
        || /^#\/(?:$|home(?:-concept)?(?:$|[?#]))/.test(window.location.hash || ''),
      mobileHomeHeaderReady:
        typeof window === 'undefined'
        || !/^#\/(?:$|home(?:-concept)?(?:$|[?#]))/.test(window.location.hash || ''),
      homeVideoCursor: false,
      viewOriginCache: [],
      viewReturnTarget: '',
      viewReturnScrollY: 0,
      siteBackTarget: '',
      siteBackLabel: '',
      siteBackScrollY: 0,
      siteBackAskReturn: false,
      aiCompletion: null,
      viewJobNotice: null,
      mobileMenuOpen: false,
      mobileMenuClosing: false,
      mobileMenuNavigating: false,
      mobileMenuItems: [
        { to: '/home', label: 'Home', index: 0 },
        { to: '/education', label: 'Study', index: 1 },
        { to: '/experience', label: 'Work', index: 2 },
        { to: '/research', label: 'Research', index: 3 },
        { to: '/projects', label: 'Builds', index: 4 },
        { to: '/fragments', label: 'Fragments', index: 5 },
        { to: '/views', label: 'Views', index: 6, view: true },
      ],
    };
  },
  computed: {
    dockPage() {
      const project = this.detailSourceId && projectById(this.detailSourceId.replace('projects:', ''));
      const tab = this.mobileMenuItems.find(({ to }) => to === this.$route.path);
      const viewTitle = this.routePageTitle.path === this.$route.path ? this.routePageTitle.title : '';
      const title = project?.title || (this.isGeneratedViewRoute
        ? viewTitle
        : tab?.label || this.$route.meta.title?.replace(/ - (H\.W\.|Han-yu Wang)$/, '')) || '';
      return {
        title: title.split(/[:：]/, 1)[0].trim(),
        isDetail: !!project || this.isGeneratedViewRoute,
      };
    },
    previewMode() {
      return this.$route.query.preview === '1';
    },
    isArchivedHome() {
      return this.$route.path === '/archive/home';
    },
    isIntroRoot() {
      return this.isArchivedHome;
    },
    isHomeRoute() {
      return this.$route.path === '/' || this.$route.path === '/home' || this.isArchivedHome;
    },
    isCv() {
      return this.$route.path === '/cv';
    },
    isRoomRoute() {
      return this.$route.path === '/room';
    },
    isGeneratedViewRoute() {
      return isGeneratedViewPath(this.$route.path);
    },
    showScrollProgress() {
      return !this.isCv && !['/home', '/', '/travels'].includes(this.$route.path);
    },
    viewNoticeHost() { return viewProgressPresence.host; },
    activeViewJob() {
      return this.viewJobNotice || (viewComposeState.active ? { startedAt: viewComposeState.startedAt, surface: viewComposeState.surface } : null);
    },
    showViewJobNotice() {
      return !!this.activeViewJob && viewProgressPresence.inlineVisible === 0;
    },
    detailSourceId() {
      return {
        '/playbook': 'projects:insurance',
        '/ragflow-pipeline': 'projects:pwcRag',
        '/ai-it-operations': 'projects:pwcItOps',
        '/document-review': 'projects:pwcDocumentReview',
        '/lexaday': 'projects:lexaday',
        '/dealtracker': 'projects:dealtracker',
        '/stride': 'projects:stride',
        '/scrollytelling': 'projects:scrollytelling',
        '/early': 'projects:early',
        '/aipersonalarchive': 'projects:searchstory',
        '/viewgallery': 'projects:searchstory',
      }[this.$route.path] || '';
    },
    routeTransitionName() {
      // The menu is already an opaque transition surface. Swapping the route
      // immediately beneath it prevents the previous page from reappearing as
      // the surface retracts.
      if (this.mobileMenuNavigating) return 'mobile-menu-route-swap';
      // Home owns a more detailed entrance sequence. A second route-level fade
      // made its mobile sections look painted once before their own reveal.
      if (this.isHomeRoute) return 'home-canvas';
      if (isGeneratedViewPath(this.$route.path)) return 'view-compose';
      return ['/research', '/projects', '/fragments', '/views'].includes(this.$route.path)
        ? 'fade-slide'
        : 'route-fade';
    }
  },
  watch: {
    '$route.query.preview'(value) {
      this.applyPreviewScrollContainment(value === '1');
    },
    '$route.query.surface'() {
      this.applyPreviewScrollContainment(this.previewMode);
    },
    '$route.path'(path, previousPath) {
      this.homeVideoCursor = false;
      if (path !== '/' && path !== '/home') {
        this.homeArrivalPending = false;
        this._cancelHomeIntroduction?.();
      }
      if (this.mobileMenuOpen && !this.mobileMenuNavigating) {
        this.closeMobileMenu({ restoreFocus: false });
      }
      if (previousPath === '/archive/home' && path !== '/archive/home') {
        this.$nextTick(this.resetIntroChromeStyles);
      }
      this.prepareMobileHomeHeaderMaterial(path);
    },
    isCv(onCv, wasCv) {
      if (this._cvChromeRevealTimer) {
        clearTimeout(this._cvChromeRevealTimer);
        this._cvChromeRevealTimer = null;
      }
      if (onCv) {
        // Hide the chrome the moment the route changes, before the page
        // transition runs, so the header never flashes on the way in.
        this.cvChromeHidden = true;
        this.pauseMusicForCv();
      } else if (wasCv || this.cvChromeHidden) {
        this._cvChromeRevealTimer = setTimeout(() => {
          this._cvChromeRevealTimer = null;
          if (!this.isCv) {
            this.cvChromeHidden = false;
            this.$nextTick(() => this.scheduleNavTabsLayout({ retries: 4 }));
          }
        }, 280);
      }
    }
  },
  mounted() {
    this._releaseHomeArrival = () => {
      this.homeArrivalPending = false;
      this._cancelHomeIntroduction?.();
    };
    window.addEventListener('home-entrance-prepared', this._releaseHomeArrival);
    this.prepareMobileHomeHeaderMaterial(this.$route.path);
    if (!this.initialChromeReady && !this.isCv) {
      this.$nextTick(() => {
        const header = this.$el.querySelector('.header');
        const footer = this.$el.querySelector('.footer');
        const reduceMotion = window.matchMedia
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) {
          this.initialChromeReady = true;
        } else {
          [header, footer].forEach((element) => {
            if (!element) return;
            element.style.opacity = '0';
            element.style.transform = element === header ? 'translateY(-8px)' : 'translateY(8px)';
          });
          this.initialChromeReady = true;
          this.$nextTick(() => {
            this._initialChromeAnimations = [
              animate(header, { opacity: [0, 1], transform: ['translateY(-8px)', 'translateY(0px)'] }, { type: 'spring', bounce: 0, duration: 0.42 }),
              animate(footer, { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0px)'] }, { type: 'spring', bounce: 0, duration: 0.46, delay: 0.08 }),
            ];
          });
        }
      });
    }
    window.addEventListener('ask-origin-change', this.syncSiteBackState);
    window.addEventListener('museum-return-origin', this.returnFromMuseum);
    this._removeViewOriginGuard = this.$router.beforeEach((to, from) => {
      // Header tabs are top-level navigation: entering a page through a tab
      // clears any pending origin so no back control follows the visitor.
      const tabNavigation = consumeTabNavigation();
      if (!to.meta.monochromeHome || getRouteReturn(to.fullPath)) prepareHomeTabOpening(null);
      const askDeparture = consumeAskSourceDeparture();
      this._askSourceNavigation = Boolean(askDeparture && askDeparture.route === from.fullPath);
      // The hall retains a separate origin across its own rooms and Read/Visit.
      // Capture header-tab arrivals too, before their normal back state clears.
      if (isMuseumRoute(to.path) && !isMuseumRoute(from.path) && from.matched.length && !to.query.embedded) {
        rememberMuseumOrigin({ route: from.fullPath, label: siteOriginLabel(from), scrollY: window.scrollY,
          collectionState: this.collectionOriginState(from), viewportAnchor: this.siteOriginViewportAnchor(from, to),
          ...(this._askSourceNavigation ? askDeparture : {}) });
      }
      if (this._askSourceNavigation) {
        setSiteOrigin({ collectionState: this.collectionOriginState(from), ...askDeparture });
      } else if (tabNavigation || (isMuseumRoute(to.path) && isMuseumRoute(from.path) && prefersMuseum())) {
        clearSiteOrigin();
      } else if (
        from.matched.length > 0
        && to.path !== from.path
      ) {
        // Any other cross-route jump records where the visitor came from and
        // their exact scroll position, so the destination can offer a precise
        // return. Museum travel keeps its separate hall origin.
        setSiteOrigin({
          route: from.fullPath,
          label: siteOriginLabel(from),
          scrollY: window.scrollY,
          collectionState: this.collectionOriginState(from),
          viewportAnchor: this.siteOriginViewportAnchor(from, to),
        });
      }
      const enteringView = isGeneratedViewPath(to.path) && !isGeneratedViewPath(from.path);
      // A fresh full load straight into a View (from.matched is empty) has no
      // in-app origin to record; the View page falls back to its ?from query.
      if (enteringView && from.matched.length > 0) {
        setViewOriginRoute(from.fullPath);
        const component = from.matched[from.matched.length - 1]?.components?.default;
        const componentName = component?.name || component?.__name || '';
        this.viewOriginCache = componentName ? [componentName] : [];
      } else if (!isGeneratedViewPath(to.path) && !isGeneratedViewPath(from.path)) {
        this.viewOriginCache = [];
      }
      return true;
    });
    this._removeViewReturnHook = this.$router.afterEach((to, from, failure) => {
      if (!failure && to.path !== from.path) {
        this.beginRouteProgressTransition();
        // Reused route components have no enter transition to wait for.
        const component = route => route.matched[route.matched.length - 1]?.components?.default;
        if (component(to) === component(from)) {
          this.finishRouteProgressTransition();
          this.$nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => markRouteArrived(to.fullPath))));
        }
      }
      // History state is available as soon as navigation commits. Sync before
      // the destination renders so its own back link never flashes beside the
      // View return control.
      if (window.history.state && window.history.state.forward && !this._askSourceNavigation) {
        // A back/forward traversal reuses an existing history entry; it is not
        // a fresh jump, so a page reached through the browser buttons keeps no
        // misleading back control.
        clearSiteOrigin();
      }
      this.syncViewReturnState();
      this.syncSiteBackState();
      this.$nextTick(this.syncViewReturnState);
      this.$nextTick(this.syncSiteBackState);
    });
    this.syncViewReturnState();
    this.syncSiteBackState();
    this.applyPreviewScrollContainment(this.previewMode);
    this._cleanupMediaRails = initCircularMediaRails();
    this.script1 = document.createElement('script');
    this.script1.src = '/assets/js/default.js';
    this.script1.async = true;
    document.body.appendChild(this.script1);
    this.script2 = document.createElement('script');
    this.script2.src = '/assets/js/theme-toggle.js';
    this.script2.async = true;
    document.body.appendChild(this.script2);
    this.$nextTick(this.setupDockControlSync);
    if (this.isCv) {
      this.cvChromeHidden = true;
      this.$nextTick(this.pauseMusicForCv);
    }
    this._onScroll = this.handleScroll;
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this.handleScroll();

    this._onDirectHomeNavRevealed = () => {
      this.directHomeNavHidden = false;
      this.$nextTick(() => {
        this.refreshMarginDockThreshold();
        this.updateMarginDock();
      });
    };
    window.addEventListener('home-direct-nav-revealed', this._onDirectHomeNavRevealed);
    this._onOpenSiteAsk = event => this.$refs.search?.openAsk(event?.detail?.question || '');
    window.addEventListener('open-site-ask', this._onOpenSiteAsk);
    this._onOpenSiteView = event => {
      const detail = event?.detail;
      if (detail?.compose && detail.question) this.$refs.search?.openViewFromAsk(detail.question, { surface: detail.surface || 'palette', returnContext: detail.returnContext });
      else this.$refs.search?.openView('');
    };
    window.addEventListener('open-site-view', this._onOpenSiteView);
    this._onOpenSiteSearch = () => {
      const palette = this.$refs.search;
      if (!palette) return;
      palette.openPalette('site_button');
      palette.setMode('search');
    };
    window.addEventListener('open-site-search', this._onOpenSiteSearch);
    this._onAiCompletion = (event) => this.showAiCompletion(event.detail);
    window.addEventListener('site-ai-complete', this._onAiCompletion);
    this._onViewJob = (event) => this.handleViewJob(event.detail);
    window.addEventListener('site-view-job', this._onViewJob);
    const savedViewJob = loadViewJob();
    if (savedViewJob) {
      if (savedViewJob.status === 'complete') {
        this.adoptCompletedView(savedViewJob.payload, savedViewJob);
      } else {
        this.viewJobNotice = savedViewJob;
        this.resumeDetachedViewJob(savedViewJob);
      }
    }
    this._onResize = () => {
      this.syncHomeVideoCursor();
      if (window.innerWidth > 1100 && this.mobileMenuOpen) this.closeMobileMenu({ restoreFocus: false });
      this.scheduleNavTabsLayout();
      this.updateMarginDock({ instant: true, refreshThreshold: true });
    };
    window.addEventListener('resize', this._onResize, { passive: true });
    this._onMobileMenuKeydown = (event) => {
      if (!this.mobileMenuOpen) return;
      if (event.key === 'Escape') this.closeMobileMenu();
      if (event.key === 'Tab') this.trapMobileMenuFocus(event);
    };
    window.addEventListener('keydown', this._onMobileMenuKeydown);
    this.$nextTick(() => {
      this.scheduleNavTabsLayout();
      this.refreshMarginDockThreshold();
      this.updateMarginDock({ instant: true });
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.scheduleNavTabsLayout).catch(() => {});
    }
  },
  unmounted() {
    this._cancelHomeIntroduction?.();
    window.removeEventListener('ask-origin-change', this.syncSiteBackState);
    window.removeEventListener('museum-return-origin', this.returnFromMuseum);
    cancelAnimationFrame(this._routeProgressFrame);
    if (this._initialChromeAnimations) {
      this._initialChromeAnimations.forEach((animation) => animation.stop());
      this._initialChromeAnimations = null;
    }
    if (this._removeViewOriginGuard) this._removeViewOriginGuard();
    if (this._removeViewReturnHook) this._removeViewReturnHook();
    if (this._cleanupMediaRails) {
      this._cleanupMediaRails();
      this._cleanupMediaRails = null;
    }
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('home-direct-nav-revealed', this._onDirectHomeNavRevealed);
    window.removeEventListener('home-entrance-prepared', this._releaseHomeArrival);
    window.removeEventListener('open-site-ask', this._onOpenSiteAsk);
    window.removeEventListener('open-site-view', this._onOpenSiteView);
    window.removeEventListener('open-site-search', this._onOpenSiteSearch);
    window.removeEventListener('site-ai-complete', this._onAiCompletion);
    window.removeEventListener('site-view-job', this._onViewJob);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('keydown', this._onMobileMenuKeydown);
    document.documentElement.classList.remove('mobile-menu-open');
    document.body.classList.remove('mobile-menu-open');
    if (this._navLayoutRaf) {
      cancelAnimationFrame(this._navLayoutRaf);
      this._navLayoutRaf = null;
    }
    if (this._cvChromeRevealTimer) {
      clearTimeout(this._cvChromeRevealTimer);
      this._cvChromeRevealTimer = null;
    }
    if (this._mobileMenuCloseTimer) {
      clearTimeout(this._mobileMenuCloseTimer);
      this._mobileMenuCloseTimer = null;
    }
    this.cancelMarginDockAnimations();
    if (this._dockControlObserver) {
      this._dockControlObserver.disconnect();
      this._dockControlObserver = null;
    }
    // Clean up the scripts when the component is destroyed
    if (this.script1 && this.script1.parentNode) {
      this.script1.parentNode.removeChild(this.script1);
    }
    if (this.script2 && this.script2.parentNode) {
      this.script2.parentNode.removeChild(this.script2);
    }
    this.applyPreviewScrollContainment(false);
  },
  methods: {
    prepareMobileHomeHeaderMaterial(path) {
      const isHome = path === '/' || path === '/home';
      if (!isHome) {
        this.mobileHomeHeaderReady = true;
        return;
      }
      this.mobileHomeHeaderReady = false;
      this.$nextTick(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (this.isHomeRoute) this.mobileHomeHeaderReady = true;
        }));
      });
    },
    toggleMobileMenu() {
      if (this.mobileMenuClosing) return;
      if (this.mobileMenuOpen) {
        this.closeMobileMenu();
        return;
      }
      if (this._mobileMenuCloseTimer) {
        clearTimeout(this._mobileMenuCloseTimer);
        this._mobileMenuCloseTimer = null;
      }
      this.mobileMenuClosing = false;
      this.mobileMenuOpen = true;
      document.documentElement.classList.add('mobile-menu-open');
      document.body.classList.add('mobile-menu-open');
      this.$nextTick(() => this.$refs.mobileMenuPanel?.focus({ preventScroll: true }));
    },
    closeMobileMenu({ restoreFocus = true } = {}) {
      if (!this.mobileMenuOpen) return;
      this._restoreMobileMenuFocus = restoreFocus;
      this.mobileMenuClosing = true;
      this.mobileMenuOpen = false;
      if (this._mobileMenuCloseTimer) clearTimeout(this._mobileMenuCloseTimer);
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 90 : 330;
      this._mobileMenuCloseTimer = window.setTimeout(() => {
        this._mobileMenuCloseTimer = null;
        this.onMobileMenuAfterLeave();
      }, duration);
    },
    onMobileMenuAfterLeave() {
      document.documentElement.classList.remove('mobile-menu-open');
      document.body.classList.remove('mobile-menu-open');
      this.mobileMenuClosing = false;
      this.mobileMenuNavigating = false;
      if (this._restoreMobileMenuFocus) {
        this.$nextTick(() => this.$refs.mobileMenuToggle?.focus({ preventScroll: true }));
      }
      this._restoreMobileMenuFocus = false;
      if (this._openSearchAfterMobileMenu) {
        this._openSearchAfterMobileMenu = false;
        this.$nextTick(this.openSearch);
      }
    },
    async navigateFromMobileMenu(path) {
      if (this.mobileMenuNavigating) return;
      this.mobileMenuNavigating = true;
      this.markTabNav();
      if (path === '/home' && !this.isHomeRoute) prepareHomeTabOpening('/home');
      if (this.$route.path !== path) {
        await this.$router.push(path).catch(() => {});
        await this.$nextTick();
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      }
      this.closeMobileMenu({ restoreFocus: false });
      if (path === '/home') settleScrollToY(0);
    },
    openSearchFromMobile() {
      if (this.mobileMenuOpen) {
        this._openSearchAfterMobileMenu = true;
        this.closeMobileMenu({ restoreFocus: false });
        return;
      }
      this.openSearch();
    },
    trapMobileMenuFocus(event) {
      const links = Array.from(this.$refs.mobileMenuNav?.querySelectorAll('a') || []);
      const toggle = this.$refs.mobileMenuToggle;
      const focusable = [toggle, ...links].filter(Boolean);
      if (!focusable.length) return;
      const current = focusable.indexOf(document.activeElement);
      if (event.shiftKey && current <= 0) {
        event.preventDefault();
        focusable.at(-1).focus();
      } else if (!event.shiftKey && (current === -1 || current === focusable.length - 1)) {
        event.preventDefault();
        focusable[0].focus();
      }
    },
    handleViewJob(task) {
      if (!task?.jobId) return;
      if (task.status === 'failed') {
        if (this.viewJobNotice?.jobId === task.jobId) this.viewJobNotice = null;
        return;
      }
      if (task.status === 'complete') {
        this.viewJobNotice = null;
        this.adoptCompletedView(task.payload, task);
        return;
      }
      if (['queued', 'composing', 'detached'].includes(task.status)) this.viewJobNotice = task;
      if (task.status === 'detached' && this._viewJobMonitorId !== task.jobId) this.resumeDetachedViewJob(task);
    },
    async resumeDetachedViewJob(task) {
      if (this._viewJobMonitorId === task.jobId) return;
      this._viewJobMonitorId = task.jobId;
      try {
        const payload = await resumeViewJob(task);
        await this.adoptCompletedView(payload, { ...task, status: 'complete' });
      } catch (error) {
        this.viewJobNotice = null;
        clearViewJob(task.jobId);
        if (error?.name !== 'AbortError') {
          const shownInPalette = this.$refs.search?.failExternalViewJob(
            task.jobId,
            'Composition is temporarily unavailable. Please try again.',
          );
          if (!shownInPalette) {
            this.showAiCompletion({ kind: 'view', label: 'Open View to try again.', surface: 'view', failed: true });
          }
        }
      } finally {
        if (this._viewJobMonitorId === task.jobId) this._viewJobMonitorId = null;
      }
    },
    async adoptCompletedView(payload, task) {
      if (!payload) return;
      if (payload.action === 'answer') {
        clearViewJob(task.jobId);
        this.viewJobNotice = null;
        const message = String(payload.message || 'Try a more specific question, theme, or perspective.').slice(0, 150);
        const shownInPalette = this.$refs.search?.failExternalViewJob(task.jobId, message);
        if (!shownInPalette) {
          this.showAiCompletion({
            kind: 'view',
            label: message,
            originRoute: task.originRoute,
            surface: task.surface,
            failed: true,
            prefill: task.message,
          });
        }
        return;
      }
      if (payload.action !== 'replace_view') return;
      try {
        const sources = await candidatesForView();
        const sanitized = sanitizeViewResponse(payload, sources);
        const validation = validateViewResponse(sanitized, sources);
        if (!validation.ok || !validation.view) return;
        const view = validation.view;
        let session = loadAskSession();
        const alreadyStored = session.currentView?.question === view.question
          && session.currentView?.title === view.title;
        if (!alreadyStored) {
          const sourceIds = [...new Set((view.sections || []).flatMap((section) =>
            (section.blocks || []).flatMap((block) => block.source_id || block.source_ids || [])))];
          session = addAskTurn(session, { role: 'user', text: task.message || view.question, source_ids: [] });
          session = addAskTurn(session, { role: 'assistant', text: 'View composed.', source_ids: sourceIds });
          session = setAskView(session, view);
          setAskOrigin(session, task.originRoute || '/home');
        }
        clearViewJob(task.jobId);
        const openedView = await this.$refs.search?.finishExternalViewJob(task.jobId);
        if (openedView) {
          this.aiCompletion = null;
          return;
        }
        this.showAiCompletion({ kind: 'view', label: view.title || task.message, originRoute: task.originRoute, surface: task.surface });
      } catch {
        // The originating surface can still finish its own validation and save.
      }
    },
    showAiCompletion(completion) {
      if (!completion || !['ask', 'view'].includes(completion.kind)) return;
      if (completion.kind === 'view' && !completion.failed && this.$route.path === (completion.route || '/view')) return;
      this.aiCompletion = {
        kind: completion.kind,
        label: String(completion.label || '').slice(0, 110),
        originRoute: String(completion.originRoute || '/home'),
        surface: completion.surface || 'palette',
        failed: completion.failed === true,
        prefill: String(completion.prefill || '').slice(0, 800),
        route: String(completion.route || ''),
      };
    },
    async openAiCompletion() {
      const completion = this.aiCompletion;
      if (!completion) return;
      this.aiCompletion = null;
      if (completion.failed) {
        this.$refs.search?.openView(completion.prefill || '');
        return;
      }
      if (completion.kind === 'view') {
        await this.$router.push(completion.route || '/view').catch(() => {});
        return;
      }
      if (completion.surface === 'home') {
        await this.$router.push('/home').catch(() => {});
      settleScrollToY(0);
        return;
      }
      this.$refs.search?.reopenCompletedAsk();
    },
    async openActiveViewJob() {
      const task = this.activeViewJob;
      if (!task) return;
      if (task.surface !== 'home') {
        this.$refs.search?.openViewJob(task);
        return;
      }
      this.$refs.search?.finishClose();
      const snapshot = loadHomeAskSnapshot();
      const context = task.returnContext || {};
      const scrollY = Math.max(0, Number(context.pageY ?? snapshot?.pageY) || 0);
      if (snapshot && Number.isFinite(context.answerTop)) {
        saveHomeAskSnapshot({ ...snapshot, answerTop: context.answerTop });
      }
      if (this.isHomeRoute) {
        settleScrollToY(scrollY);
        return;
      }
      const intent = prepareRouteReturn('/home', scrollY);
      const failure = await this.$router.push('/home').catch(error => error);
      if (failure) clearRouteReturn(intent);
    },
    markHomeNav() {
      if (!this.isHomeRoute) {
        this.markTabNav();
        prepareHomeTabOpening('/home');
      }
      if (this.isHomeRoute) settleScrollToY(0);
    },
    markTabNav() {
      markTabNavigation();
    },
    collectionOriginState(route) {
      if (!route || route.path !== '/projects' || !this.$el) return null;
      const browser = this.$el.querySelector('.projects-collection-view-stage .collection-browser');
      if (!browser) return { mode: 'expanded', openId: '' };
      const openTrigger = this.$el.querySelector(
        '.projects-collection-view-stage .collection-card-summary[aria-expanded="true"], '
          + '.projects-collection-view-stage .collection-index-row[aria-expanded="true"]'
      );
      const openEntry = openTrigger && openTrigger.closest('[id]');
      return {
        mode: browser.classList.contains('is-index') ? 'index' : 'grid',
        openId: openEntry && openEntry.id ? openEntry.id : '',
      };
    },
    siteOriginViewportAnchor(from, to) {
      if (!['/', '/home'].includes(from.path)) return null;
      const chapters = [...(this.$el?.querySelectorAll('.home-concept-page [data-story-chapter], .mono-home [data-story-chapter]') || [])];
      const chapter = chapters.filter(element => element.getBoundingClientRect().top <= window.innerHeight * 0.42).at(-1);
      if (chapter) return { selector: `#${chapter.id}`, index: 0, top: chapter.getBoundingClientRect().top };
      if (!window.matchMedia || !window.matchMedia('(max-width: 760px)').matches) return null;
      const links = Array.from(this.$el?.querySelectorAll('.concept-current a') || []);
      const destination = String(to.fullPath || to.path || '');
      let index = links.findIndex((link) => {
        const href = link.getAttribute('href') || '';
        let decodedHref = href;
        try {
          decodedHref = decodeURIComponent(href);
        } catch {
          decodedHref = href;
        }
        return decodedHref.endsWith(`#${destination}`) || decodedHref.endsWith(destination);
      });
      if (index < 0 && document.activeElement) index = links.indexOf(document.activeElement.closest?.('.concept-current a'));
      if (index < 0) return null;
      const header = this.$el?.querySelector('.header');
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      return {
        selector: '.concept-current a',
        index,
        top: Math.max(links[index].getBoundingClientRect().top, headerBottom + 12),
      };
    },
    syncSiteBackState() {
      const origin = getSiteOrigin();
      this.siteBackTarget = origin && origin.route ? origin.route : '';
      this.siteBackLabel = origin && origin.label ? origin.label : '';
      this.siteBackScrollY = (origin && origin.scrollY) || 0;
      this.siteBackAskReturn = Boolean(origin?.askReturn);
    },
    async returnFromMuseum(event) {
      const origin = event.detail;
      if (!origin?.route || !this.$router.resolve(origin.route).matched.length) { revealViewTransition(); return; }
      setSiteOrigin(origin);
      this.syncSiteBackState();
      try { await this.returnToSiteOrigin(); } finally { revealViewTransition(); }
    },
    async returnToSiteOrigin() {
      const target = this.siteBackTarget;
      if (!target) return;
      // Capture before navigating: afterEach resets the back state once the
      // destination commits, so reading it later would restore to 0 (top).
      const scrollY = this.siteBackScrollY;
      const origin = getSiteOrigin();
      const collectionState = origin && origin.collectionState;
      const viewportAnchor = origin && origin.viewportAnchor;
      const askReturn = origin?.askReturn;
      if (collectionState) {
        prepareCollectionReturn(collectionState);
        this._siteOriginReturnScrollY = scrollY;
      }
      // Returning through the control is a deliberate top-level move, not a
      // fresh jump, so the destination must not inherit a new back control.
      markTabNavigation();
      const resolvedTarget = this.$router.resolve(target).fullPath;
      if (this.$route.fullPath === resolvedTarget) {
        consumeTabNavigation();
        clearSiteOrigin();
        this.syncSiteBackState();
        if (askReturn?.surface === 'palette') this.$refs.search?.restoreFromSource(askReturn.snapshot);
        else settleScrollToY(scrollY);
        return;
      }
      const returnIntent = prepareRouteReturn(resolvedTarget, scrollY);
      if (returnIntent) returnIntent.viewportAnchor = viewportAnchor;
      const failure = await this.$router.push(target).catch(error => error);
      if (failure) {
        clearRouteReturn(returnIntent);
        return;
      }
      if (returnIntent) {
        await returnIntent.ready;
        if (this.$route.fullPath !== resolvedTarget) return;
      }
      if (askReturn?.surface === 'palette') this.$refs.search?.restoreFromSource(askReturn.snapshot);
    },
    syncViewReturnState() {
      const state = window.history.state || {};
      const target = String(state.viewReturnRoute || '');
      this.viewReturnTarget = target && isGeneratedViewPath(target.split(/[?#]/)[0])
        ? target
        : '';
      this.viewReturnScrollY = Number(state.viewReturnScrollY) || 0;
    },
    async returnToSourceView() {
      const target = this.viewReturnTarget;
      if (!target) return;
      // Remember where the visitor was in the View so it can be restored after
      // the View page reloads its sources (its load flow otherwise scrolls top).
      const scrollY = this.viewReturnScrollY;
      setPendingViewReturnScroll(scrollY);
      const state = window.history.state || {};
      if (state.back === target) {
        this.$router.back();
        return;
      }
      await this.$router.push(target).catch(() => {});
      settleScrollToY(scrollY);
    },
    beginHomeIdentityPress(event) {
      const element = event.currentTarget;
      if (!element) return;
      if (!this._homeIdentityPresses) this._homeIdentityPresses = new WeakMap();
      const previous = this._homeIdentityPresses.get(element);
      previous?.animation?.stop?.();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const state = {
        startedAt: performance.now(),
        committing: false,
        reducedMotion,
        inlineTransform: element.style.transform,
        inlineScale: element.style.scale,
        inlineOpacity: element.style.opacity,
      };
      if (reducedMotion) {
        element.style.opacity = '0.62';
      } else {
        state.animation = animate(element, { scale: 0.97 }, { duration: 0.075, ease: [0.23, 1, 0.32, 1] });
      }
      this._homeIdentityPresses.set(element, state);
    },
    cancelHomeIdentityPress(event) {
      const element = event.currentTarget;
      const state = this._homeIdentityPresses?.get(element);
      if (!state || state.committing) return;
      state.animation?.stop?.();
      this._homeIdentityPresses.delete(element);
      if (state.reducedMotion) {
        element.style.opacity = state.inlineOpacity;
        return;
      }
      const release = animate(element, { scale: 1 }, { duration: 0.11, ease: [0.23, 1, 0.32, 1] });
      release.finished.then(() => {
        element.style.transform = state.inlineTransform;
        element.style.scale = state.inlineScale;
      }).catch(() => {});
    },
    async navigateHomeWithPress(event, { introduction = false } = {}) {
      const element = event.currentTarget;
      if (!element) return;
      let state = this._homeIdentityPresses?.get(element);
      if (!state) {
        this.beginHomeIdentityPress(event);
        state = this._homeIdentityPresses?.get(element);
      }
      if (!state || state.committing) return;
      state.committing = true;
      state.animation?.stop?.();

      const elapsed = performance.now() - state.startedAt;
      const minimumPress = state.reducedMotion ? 70 : 120;
      if (elapsed < minimumPress) {
        await new Promise((resolve) => window.setTimeout(resolve, minimumPress - elapsed));
      }

      if (state.reducedMotion) {
        element.style.opacity = state.inlineOpacity;
        await new Promise((resolve) => window.setTimeout(resolve, 55));
      } else {
        const release = animate(element, { scale: 1 }, { duration: 0.11, ease: [0.23, 1, 0.32, 1] });
        await release.finished.catch(() => {});
        element.style.transform = state.inlineTransform;
        element.style.scale = state.inlineScale;
      }

      this._homeIdentityPresses.delete(element);
      if (introduction) {
        this._cancelHomeIntroduction?.();
        const cancel = requestHomeIntroduction();
        this._cancelHomeIntroduction = cancel;
        this.viewOriginCache = [];
        this.homeArrivalPending = !state.reducedMotion;
        if (!this.isHomeRoute) prepareHomeTabOpening('/');
        const failure = await this.$router.push('/').catch(error => error);
        if (failure) {
          cancel();
          if (this._cancelHomeIntroduction === cancel) this.homeArrivalPending = false;
        }
        return;
      }
      if (!this.isHomeRoute) prepareHomeTabOpening('/home');
      await this.$router.push('/home').catch(() => {});
      settleScrollToY(0);
    },
    applyPreviewScrollContainment(active) {
      // Inside a preview iframe the inner page must not pass scroll chaining
      // back to the page behind the preview window at its scroll boundary.
      document.documentElement.classList.toggle('preview-mode', active);
      document.documentElement.classList.toggle('home-glass-preview', active && this.$route.query.surface === 'glass');
    },
    resetIntroChromeStyles() {
      if (!this.$el) return;
      const selectors = [
        '.header-title > a',
        '.header-subtitle',
        '.menu-content a',
        '.social-icons button',
        '.footer a',
        '.footer-description',
      ];
      this.$el.querySelectorAll(selectors.join(',')).forEach((element) => {
        ['color', 'opacity', 'transform', 'transition'].forEach((property) => {
          element.style.removeProperty(property);
        });
        const glyph = element.querySelector('i');
        if (glyph) glyph.style.removeProperty('color');
      });
    },
    beginRouteProgressTransition() {
      this.routeProgressPending = true;
      this.marginDockPinned = false;
      this.dockHoverCount = 0;
      this.setMarginDock(false, { instant: true });
      cancelAnimationFrame(this._routeProgressFrame);
    },
    finishRouteProgressTransition() {
      cancelAnimationFrame(this._routeProgressFrame);
      // Keep the old page's ratio hidden until layout and scroll restoration
      // have painted, including routes whose CSS transition has no duration.
      this._routeProgressFrame = requestAnimationFrame(() => {
        this._routeProgressFrame = requestAnimationFrame(() => {
          this._routeProgressFrame = null;
          this.routeProgressPending = false;
          this.refreshMarginDockThreshold();
          this.handleScroll();
        });
      });
    },
    beforeRouteEnter(el) {
      const route = this.$route.fullPath;
      const returnIntent = getRouteReturn(route);
      if (returnIntent) el.classList.add('is-restoring-position');
      this.$nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.$route.fullPath !== route) {
          clearRouteReturn(returnIntent);
          el.classList.remove('is-restoring-position');
          return;
        }
        if (returnIntent) {
          const anchor = returnIntent.viewportAnchor;
          const element = anchor && document.querySelectorAll(anchor.selector || '')[anchor.index];
          if (element) returnIntent.top = Math.max(0, window.scrollY + element.getBoundingClientRect().top - Number(anchor.top || 0));
          markReturnRouteEntered(route);
          requestAnimationFrame(() => el.classList.remove('is-restoring-position'));
        }
        markRouteArrived(route);
      })));
      const enteringCv = el && el.classList && el.classList.contains('cv-root');
      this.cvChromeHidden = enteringCv;
      if (!enteringCv) {
        this.$nextTick(() => this.scheduleNavTabsLayout({ retries: 4 }));
      }
      if (this.$route.path === '/projects' && this._siteOriginReturnScrollY != null) {
        const scrollY = this._siteOriginReturnScrollY;
        this._siteOriginReturnScrollY = null;
        const restore = () => window.scrollTo({ top: scrollY, behavior: 'auto' });
        restore();
        requestAnimationFrame(() => requestAnimationFrame(restore));
      }
    },
    openSearch() {
      this.$refs.search?.openPalette('pointer');
    },
    setupDockControlSync() {
      const theme = document.getElementById('toggle-theme');
      const music = document.getElementById('music-icon');
      if (!theme || !music) return;
      this.syncDockControlState();
      this._dockControlObserver = new MutationObserver(this.syncDockControlState);
      [theme, music].forEach((button) => {
        this._dockControlObserver.observe(button, {
          attributes: true,
          subtree: true,
          attributeFilter: ['aria-label', 'class', 'title'],
        });
      });
    },
    syncDockControlState() {
      const theme = document.getElementById('toggle-theme');
      const music = document.getElementById('music-icon');
      const themeIcon = theme && theme.querySelector('i');
      const musicIcon = music && music.querySelector('i');
      if (themeIcon) this.dockThemeIconClass = themeIcon.className;
      if (musicIcon) this.dockMusicIconClass = musicIcon.className;
      if (theme) this.dockThemeLabel = theme.getAttribute('aria-label') || 'Toggle theme';
      if (music) this.dockMusicLabel = music.getAttribute('aria-label') || 'Play music';
    },
    toggleThemeFromDock() {
      const theme = document.getElementById('toggle-theme');
      if (theme) theme.click();
    },
    toggleMusicFromDock() {
      const music = document.getElementById('music-icon');
      if (music) music.click();
    },
    releaseDockPointerFocus(event) {
      if (event.currentTarget) event.currentTarget.blur();
    },
    handleScroll() {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      this.scrollPct = max > 0 ? Math.min(Math.max(el.scrollTop / max, 0), 1) : 0;
      this.syncHomeVideoCursor();
      this.updateMarginDock();
    },
    syncHomeVideoCursor() {
      const hero = this.isHomeRoute && window.innerWidth <= 760
        && this.$el?.querySelector('.home-concept-page .story-hero');
      const ask = hero && this.$el.querySelector('#story-mobile-ask');
      const header = hero && this.$el.querySelector('.header');
      // Ask overlaps the video's lower edge. Switch as its surface reaches
      // the header, while each content region keeps its own cursor colours.
      this.homeVideoCursor = Boolean(hero && header
        && Math.min(hero.getBoundingClientRect().bottom, ask?.getBoundingClientRect().top ?? Infinity)
          > header.getBoundingClientRect().bottom);
    },
    canUseMarginDock() {
      const content = this.$el?.querySelector('.content-container');
      return !!content && !this.cvChromeHidden
        && !this.directHomeNavHidden
        && this.$route.path !== '/'
        && this.$route.path !== '/home'
        && this.$route.path !== '/travels'
        && !this.isArchivedHome
        && window.matchMedia('(min-width: 1101px) and (min-height: 620px) and (hover: hover) and (pointer: fine)').matches
        && marginDockFits(window.innerWidth, content.getBoundingClientRect());
    },
    marginDockElements() {
      if (!this.$el) return [];
      const identity = this.$el.querySelector('.margin-dock-home');
      const pageTitle = this.$el.querySelector('.margin-dock-page');
      const controls = Array.from(this.$el.querySelectorAll('.margin-dock-controls .margin-dock-control'));
      return [
        identity && { element: identity, kind: 'identity', order: 0 },
        pageTitle && { element: pageTitle, kind: 'menu', order: 0 },
        ...controls.map((element, order) => ({ element, kind: 'control', order })),
      ].filter(Boolean);
    },
    refreshMarginDockThreshold() {
      if (this.marginDockActive || !this.$el) return;
      const header = this.$el.querySelector('.header');
      if (!header) return;
      this._marginDockThreshold = header.offsetTop + header.offsetHeight + 12;
    },
    updateMarginDock(options = {}) {
      const { instant = false, refreshThreshold = false } = options;
      // The outgoing scroll offset belongs to the previous page. Wait for
      // the destination's layout and scroll restoration before showing a rail.
      if (this.routeProgressPending) return;
      if (refreshThreshold) this.refreshMarginDockThreshold();
      if (!this.canUseMarginDock()) {
        this.marginDockPinned = false;
        this.dockHoverCount = 0;
        this.setMarginDock(false, { instant: true });
        return;
      }
      if (!Number.isFinite(this._marginDockThreshold)) {
        this.refreshMarginDockThreshold();
      }
      if (!Number.isFinite(this._marginDockThreshold)) return;

      const exitThreshold = Math.max(0, this._marginDockThreshold - 42);
      const shouldDock = this.marginDockActive
        ? (window.scrollY > exitThreshold || this.marginDockPinned)
        : window.scrollY > this._marginDockThreshold;
      this.setMarginDock(shouldDock, { instant });
    },
    cancelMarginDockAnimations() {
      if (!this._marginDockAnimations) return;
      this._marginDockAnimations.forEach((animation) => animation.stop());
      this._marginDockAnimations = [];
    },
    holdDockPin() {
      if (!this.canUseMarginDock()) return;
      this.dockHoverCount += 1;
    },
    releaseDockPin() {
      if (!this.canUseMarginDock()) return;
      this.dockHoverCount = Math.max(0, this.dockHoverCount - 1);
      if (this.dockHoverCount > 0) return;
      this.marginDockPinned = false;
      this.updateMarginDock();
    },
    setMarginDock(active, options = {}) {
      const currentTarget = this._marginDockTarget ?? this.marginDockActive;
      const { instant = false } = options;
      if (currentTarget === active && (!instant || (!active && !this.marginDockActive))) return;
      const transitionToken = this._dockSettleToken = (this._dockSettleToken || 0) + 1;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const elements = this.marginDockElements();
      this._marginDockTarget = active;
      this.cancelMarginDockAnimations();
      window.dispatchEvent(new CustomEvent('margin-dock-visibility', { detail: { active, instant: instant || reduceMotion } }));

      if (instant || reduceMotion || (!active && this._dockPreparing)) {
        this._dockPreparing = false;
        this.marginDockActive = active;
        elements.forEach(({ element }) => {
          element.style.transform = '';
          element.style.opacity = '';
          element.style.visibility = '';
        });
        this._dockWasAnimated = new WeakSet();
        return;
      }

      const enteringFromHidden = active && !this.marginDockActive;
      if (enteringFromHidden) {
        // v-show must expose layout for measurement without painting the
        // resting pose while Motion prepares its first keyframes.
        this._dockPreparing = true;
        elements.forEach(({ element }) => {
          element.style.visibility = 'hidden';
          element.style.transform = '';
          element.style.opacity = '';
          this._dockResting?.delete(element);
        });
        this.marginDockActive = true;
      }

      if (!this._dockResting) this._dockResting = new WeakMap();
      if (!this._dockWasAnimated) this._dockWasAnimated = new WeakSet();

      this.$nextTick(async () => {
        if (this._dockSettleToken !== transitionToken) return;
        if (enteringFromHidden) {
          this.$refs.marginDockTitle?.fit();
          await this.$nextTick();
        }
        requestAnimationFrame(() => {
          if (this._dockSettleToken !== transitionToken) return;
          const animations = [];
          elements.forEach(({ element, kind, order }) => {
            if (!element.isConnected) return;
            const rect = element.getBoundingClientRect();
            if (!rect.width || !rect.height) return;

            // Capture the CSS resting transform once, before any spring leaves
            // an inline style behind. Stopping a Motion animation keeps the
            // element where it was, so later reads give the live on-screen
            // value and a scroll reversal starts from there, never from a
            // logical target.
            let rest = this._dockResting.get(element);
            if (!rest) {
              const atRest = currentTranslation(element);
              rest = { ...atRest, opacity: getComputedStyle(element).opacity };
              this._dockResting.set(element, rest);
            }
            const current = currentTranslation(element);
            const edgeOffset = kind === 'control'
              ? window.innerWidth - rect.left + 16
              : -(rect.right + 16);

            // A fresh reveal uses the latest resting geometry. Reversing a
            // visible animation continues from its current on-screen pose.
            const firstTime = enteringFromHidden || !this._dockWasAnimated.has(element);
            const fromX = active
              ? (firstTime ? rest.x + edgeOffset : current.x)
              : current.x;
            const fromY = active
              ? (firstTime ? rest.y : current.y)
              : current.y;
            const toX = active ? rest.x : rest.x + edgeOffset;
            const toY = rest.y;
            const fromOpacity = active
              ? (firstTime ? 0 : Number(getComputedStyle(element).opacity))
              : Number(getComputedStyle(element).opacity);
            const toOpacity = active ? Number(rest.opacity) : 0;

            // Spring response, not duration: critically damped (no overshoot),
            // snappier for the smaller pieces and for the exit.
            const response = active
              ? ({ identity: 0.4, subtitle: 0.36, menu: 0.32, control: 0.3 }[kind] || 0.34)
              : 0.24;
            const delay = active
              ? (kind === 'identity'
                ? 0
                : kind === 'subtitle'
                  ? 0.05
                  : kind === 'menu'
                    ? 0.06
                    : order * 0.02)
              : (kind === 'menu' ? Math.max(0, 5 - order) * 0.012 : 0);

            // Motion resolves keyframes asynchronously. Hold the exact first
            // pose in CSS until the animation takes over, including its delay.
            element.style.transform = `translate3d(${fromX}px, ${fromY}px, 0)`;
            element.style.opacity = String(fromOpacity);
            const animation = animate(element, {
              transform: [
                `translate3d(${fromX}px, ${fromY}px, 0)`,
                `translate3d(${toX}px, ${toY}px, 0)`,
              ],
              opacity: [fromOpacity, toOpacity],
            }, {
              type: 'spring',
              bounce: 0,
              duration: response,
              delay,
            });
            this._dockWasAnimated.add(element);
            animations.push(animation);
          });
          this._marginDockAnimations = animations;
          this._dockPreparing = false;
          elements.forEach(({ element }) => { element.style.visibility = ''; });

          Promise.all(animations.map((animation) => animation.finished.catch(() => {})))
            .then(() => {
              if (this._marginDockAnimations !== animations) return;
              if (!active && this._marginDockTarget === false) {
                this.marginDockActive = false;
              }
              animations.forEach((animation) => animation.stop());
              this._marginDockAnimations = [];
              if (active) {
                // The springs leave inline transform/opacity pinned on the
                // elements, which blocks the stylesheet hover and active
                // feedback. Once the dock has settled, clear them so CSS
                // drives the resting state. A delayed spring can commit its
                // final keyframe after the completion fires, so re-clear on a
                // short guarded timer until every element has settled.
                const settleToken = this._dockSettleToken;
                const clearInline = () => {
                  if (this._dockSettleToken !== settleToken) return;
                  if (this._marginDockAnimations && this._marginDockAnimations.length) return;
                  elements.forEach(({ element }) => {
                    if (!element.isConnected) return;
                    element.style.transform = '';
                    element.style.opacity = '';
                  });
                };
                clearInline();
                [160, 480].forEach((ms) => setTimeout(clearInline, ms));
              }
              if (!active) this.$nextTick(this.refreshMarginDockThreshold);
            });
        });
      });
    },
    scheduleNavTabsLayout(options = {}) {
      const retries = options.retries || 0;
      if (this._navLayoutRaf) cancelAnimationFrame(this._navLayoutRaf);
      this._navLayoutRaf = requestAnimationFrame(() => {
        this._navLayoutRaf = null;
        const updated = this.updateNavTabsLayout();
        if (!updated && retries > 0) {
          this.scheduleNavTabsLayout({ retries: retries - 1 });
        }
      });
    },
    updateNavTabsLayout() {
      if (this.cvChromeHidden) return false;

      const header = this.$el && this.$el.querySelector('.header');
      const menu = this.$el && this.$el.querySelector('.menu');
      const tabs = this.$el && this.$el.querySelector('.menu-content');
      const icons = this.$el && this.$el.querySelector('.social-icons');
      if (!header || !menu || !tabs || !icons) return false;
      if (getComputedStyle(header).display === 'none') return false;

      const items = Array.from(tabs.querySelectorAll('.menu-item'));
      if (items.length !== 6) return false;

      const menuWidth = menu.getBoundingClientRect().width;
      const iconsWidth = icons.getBoundingClientRect().width;
      let singleRowWidths;
      let wrappedWidths;
      try {
        tabs.classList.add('nav-tabs-measuring');
        singleRowWidths = items.map((item) => item.getBoundingClientRect().width);
        // Wrapped navigation hides Views, so measure that actual layout separately.
        tabs.classList.remove('nav-tabs-measuring');
        tabs.classList.add('nav-tabs-measuring-wrapped');
        wrappedWidths = items.map((item) => item.getBoundingClientRect().width);
      } finally {
        tabs.classList.remove('nav-tabs-measuring', 'nav-tabs-measuring-wrapped');
      }
      if (menuWidth <= 0 || iconsWidth <= 0 || [...singleRowWidths, ...wrappedWidths].some((width) => width <= 0)) {
        return false;
      }

      const gap = parseFloat(getComputedStyle(tabs).columnGap) || 0;
      const availableWidth = Math.max(0, menuWidth - iconsWidth);
      const layoutWidth = (cols, widths) => {
        const colWidths = Array.from({ length: cols }, (_, col) => {
          let max = 0;
          for (let i = col; i < widths.length; i += cols) {
            max = Math.max(max, widths[i]);
          }
          return max;
        });
        return colWidths.reduce((sum, width) => sum + width, 0) + gap * (cols - 1);
      };

      if (layoutWidth(6, singleRowWidths) <= availableWidth) {
        this.navTabsLayout = 'one';
      } else if (layoutWidth(3, wrappedWidths) <= availableWidth) {
        this.navTabsLayout = 'two';
      } else {
        this.navTabsLayout = 'three';
      }
      return true;
    },
    pauseMusicForCv() {
      const music = document.getElementById('background-music');
      if (music && !music.dataset.initialized) music.dataset.pauseOnReady = 'true';
      window.dispatchEvent(new CustomEvent('site-music-command', {
        detail: { action: 'pause', fadeOut: 600 }
      }));
    }
  }
}
</script>

<style>
    @import url('./assets/css/main.css');
    @import url('./assets/css/surface-polish.css');
    @import url('./assets/css/syntax.css');
    @import url('./assets/css/dark-theme.css');
    @import url('./assets/css/galleryScrollbars.css');

    .is-restoring-position,
    .container:has(.is-restoring-position) > .footer { visibility: hidden !important; }
    html:has(.home-concept-page),
    body:has(.home-concept-page) {
      overflow-x: clip;
      overscroll-behavior-x: none;
    }
    body:has(.home-concept-page) { overflow-anchor: none; }
    .container:has(.home-concept-page) > .site-back-global { display: none; }

    @media (min-width: 761px) {
      html:has(.home-page),
      body:has(.home-page) {
        overflow-y: hidden;
      }
      .header.home-canvas-layout {
        margin-bottom: 0;
      }
      .container:not(:has(.home-concept-page)):not(:has(.home-page)) > .header.home-canvas-layout {
        margin-bottom: clamp(2rem,4vh,3rem);
      }
      .container:has(.home-page) {
        display: flex;
        min-height: 100dvh;
        flex-direction: column;
      }
      .content-container:has(.home-page) {
        position: relative;
        left: 50%;
        display: flex;
        width: 100vw;
        min-height: 0;
        margin-right: -50vw;
        margin-left: -50vw;
        margin-bottom: 0;
        flex: 1 1 auto;
        align-items: center;
      }
      .content-container:has(.home-page) > .home-page {
        flex: 0 0 auto;
      }
      .container:has(.home-concept-page) {
        display: flex;
        min-height: 100dvh;
        flex-direction: column;
      }
      .content-container:has(.home-concept-page) {
        position: relative;
        left: 50%;
        display: flex;
        box-sizing: border-box;
        width: 100vw;
        min-height: 0;
        margin-right: -50vw;
        margin-left: -50vw;
        margin-bottom: 0;
        padding-block: 0;
        flex: 1 1 auto;
        align-items: flex-start;
      }
      .content-container:has(.home-concept-page) > .home-concept-page {
        flex: 0 0 auto;
      }
      .footer.home-route {
        margin-top: auto;
      }
    }
    @media (max-width: 760px) {
      .container:has(.home-page) {
        --home-footer-bottom-space: max(28px, env(safe-area-inset-bottom));
        --home-footer-block-size: calc(67px + var(--home-footer-bottom-space));
      }
      .content-container:has(.home-page) {
        margin-bottom: 0;
      }
      .container:has(.home-page) > .footer {
        box-sizing: border-box;
        height: var(--home-footer-block-size);
        margin-top: 0;
        margin-bottom: 0;
        padding-bottom: var(--home-footer-bottom-space);
      }
    }
    @media (min-width: 1101px) {
      :root {
        --home-desktop-frame: clamp(128px, 10vw, 160px);
      }
      .header.home-canvas-layout {
        position: relative;
        left: 50%;
        width: min(88rem, calc(100vw - var(--home-desktop-frame)));
        max-width: none;
        translate: -50% 0;
      }
      .header.home-canvas-layout .desktop-global-nav {
        display: grid;
        grid-template-columns: max-content minmax(0, 1fr) max-content;
        align-items: end;
        width: 100%;
        margin: 1.17rem 0 0;
      }
      .header.home-canvas-layout .header-title > a {
        grid-column: 1;
        align-self: end;
        line-height: 1;
        translate: 0 6px;
      }
      .header.home-canvas-layout .header-subtitle {
        display: none;
      }
      .header.home-canvas-layout .menu {
        display: contents;
      }
      .header.home-canvas-layout .menu-content {
        grid-column: 2;
        align-self: end;
        justify-self: center;
        margin: 0;
      }
      .header.home-canvas-layout .menu-content a {
        padding-bottom: 0;
        line-height: 1;
      }
      .header.home-canvas-layout .menu-content a::after {
        bottom: -3px;
      }
      .header.home-canvas-layout .menu-view-separator {
        transform: none;
      }
      .header.home-canvas-layout .social-icons {
        grid-column: 3;
        align-self: end;
        margin: 0;
        translate: 0 3px;
      }
      .header.home-canvas-layout .site-control {
        width: 2rem;
        height: 2rem;
      }
    }
</style>

<style scoped>
    .view-return-slot {
      position: fixed;
      top: 4.55rem;
      right: max(1rem, calc((100vw - 76rem) / 2));
      z-index: 34;
    }
    .view-return-control {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      min-height: 2.75rem;
      padding: 0.5rem 0.62rem 0.58rem;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: #55524e;
      font-family: "Quattrocento Sans", sans-serif;
      font-size: 0.8rem;
      font-weight: 600;
      letter-spacing: 0.018em;
      cursor: pointer;
      isolation: isolate;
      transition: color 160ms ease, transform 120ms ease;
    }
    .view-return-control::before {
      position: absolute;
      inset: 0;
      z-index: -1;
      background: rgba(0, 0, 0, 0.045);
      content: '';
      opacity: 0;
      transform: scaleX(0.82);
      transform-origin: right center;
      transition: opacity 160ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .view-return-control::after {
      position: absolute;
      right: 0.62rem;
      bottom: 0.34rem;
      left: 1.72rem;
      height: 1px;
      background: currentColor;
      content: '';
      opacity: 0;
      transform: scaleX(0);
      transform-origin: left center;
      transition: opacity 160ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .view-return-arrow {
      color: inherit;
      font-size: 1rem;
      line-height: 1;
      transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .view-return-control:hover,
    .view-return-control:focus-visible {
      color: #242321;
    }
    .view-return-control:hover::before,
    .view-return-control:focus-visible::before {
      opacity: 1;
      transform: scaleX(1);
    }
    .view-return-control:hover::after,
    .view-return-control:focus-visible::after {
      opacity: 0.62;
      transform: scaleX(1);
    }
    .view-return-control:hover .view-return-arrow,
    .view-return-control:focus-visible .view-return-arrow {
      transform: translateX(-2px);
    }
    .view-return-control:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
    .view-return-control:active {
      transform: translateY(1px);
    }
    body.dark-mode .view-return-control {
      color: #d4d1cc;
    }
    body.dark-mode .view-return-control::before {
      background: rgba(255, 255, 255, 0.075);
    }
    body.dark-mode .view-return-control:hover,
    body.dark-mode .view-return-control:focus-visible {
      color: #f4f1ec;
    }
    .view-return-enter-active,
    .view-return-leave-active {
      transition: opacity 140ms ease, transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .view-return-enter-from,
    .view-return-leave-to {
      opacity: 0;
      transform: translateY(-5px);
    }
    .site-back-fade-enter-active,
    .site-back-fade-leave-active {
      transition: opacity 140ms ease, transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .site-back-fade-enter-from,
    .site-back-fade-leave-to {
      opacity: 0;
      transform: translateY(-5px);
    }
    @media (max-width: 1180px) {
      .view-return-slot {
        z-index: 34;
        display: flex;
        justify-content: flex-end;
        width: fit-content;
        background: rgba(255, 255, 255, 0.94);
        backdrop-filter: blur(12px) saturate(120%);
        -webkit-backdrop-filter: blur(12px) saturate(120%);
      }
      body.dark-mode .view-return-slot {
        background: rgba(29, 29, 29, 0.94);
      }
      .view-return-control {
        min-height: 2.75rem;
        padding-inline: 0.78rem;
      }
    }
    @media (max-width: 1100px) {
      .view-return-slot {
        top: calc(var(--mobile-sticky-header-height) + 0.4rem);
        z-index: 39;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .view-return-control,
      .view-return-arrow,
      .view-return-enter-active,
      .view-return-leave-active {
        transition-duration: 80ms;
      }
      .view-return-enter-from,
      .view-return-leave-to {
        transform: none;
      }
    }
    .fade-slide-enter-active {
      transition: opacity 0.18s cubic-bezier(0.23, 1, 0.32, 1), transform 0.18s cubic-bezier(0.23, 1, 0.32, 1);
    }
    .fade-slide-leave-active {
      transition: opacity 0.1s ease, transform 0.1s ease;
    }
    .fade-slide-enter-from, .fade-slide-leave-to {
      opacity: 0;
    }
    .fade-slide-enter-from {
      transform: translateY(10px);
    }
    .fade-slide-leave-active {
      transform: translateY(-6px);
      opacity: 0;
    }
    .route-fade-enter-active {
      transition: opacity 0.14s ease-out;
    }
    .route-fade-leave-active {
      transition: opacity 0.08s ease;
    }
    .route-fade-enter-from,
    .route-fade-leave-to {
      opacity: 0;
    }
    .view-compose-leave-active {
      transition: opacity 90ms ease-in, transform 100ms ease-in;
    }
    .view-compose-leave-to {
      opacity: 0;
      transform: translateY(-4px) scale(0.997);
    }
    .view-compose-enter-active {
      transition: opacity 240ms ease-out, transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
      will-change: opacity, transform;
    }
    .view-compose-enter-from {
      opacity: 0;
      transform: translateY(11px) scale(0.998);
    }
    .header.home-direct-nav .header-title > a,
    .header.home-direct-nav .header-subtitle,
    .header.home-direct-nav .menu-view-group,
    .header.home-direct-nav .menu-content a,
    .header.home-direct-nav .social-icons a,
    .header.home-direct-nav .social-icons button,
    .footer.home-direct-nav a,
    .footer.home-direct-nav .footer-description {
      color: transparent;
    }
    .header.home-arrival-pending { opacity: 0; }
    @media (prefers-reduced-motion: reduce) { .header.home-arrival-pending { opacity: 1; } }
    .header.initial-chrome-pending,
    .footer.initial-chrome-pending {
      opacity: 0;
    }
    @media (min-width: 761px) {
      .footer.home-route {
        margin-bottom: 0;
      }
      /* The route class hides the footer before Home mounts. The DOM guard
         keeps it hidden while a leaving Home canvas still occupies the page. */
      .footer.home-route,
      body:has(.home-page) .footer {
        display: none;
      }
    }
    .menu-view-link :deep(.ai-view-term),
    .margin-dock-view :deep(.ai-view-term),
    .mobile-menu-view :deep(.ai-view-term) {
      font-weight: inherit;
      letter-spacing: inherit;
    }
    .menu-view-link :deep(.ai-view-term-mark),
    .margin-dock-view :deep(.ai-view-term-mark),
    .mobile-menu-view :deep(.ai-view-term-mark) {
      color: currentColor;
    }
    .mobile-global-nav {
      display: none;
    }
    @media (max-width: 1100px) {
      :global(body) {
        margin-top: 0;
      }
      :global(body.mobile-menu-open) {
        overflow: hidden;
        overscroll-behavior: none;
      }
      .header {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 9000;
        isolation: isolate;
        box-sizing: border-box;
        width: 100vw;
        margin-left: 0;
        margin-bottom: 0.82rem;
        padding: 0;
        background: #fff;
        backdrop-filter: blur(18px) saturate(170%);
        -webkit-backdrop-filter: blur(18px) saturate(170%);
        backface-visibility: hidden;
        transform: translateZ(0);
        will-change: transform;
      }
      .container::before {
        content: '';
        display: block;
        height: calc(48px + 0.82rem);
      }
      :global(body.dark-mode .header) {
        background: #1d1d1f;
      }
      .header.home-route.home-material-ready:not(.mobile-menu-surface) {
        transition:
          background-color 280ms cubic-bezier(0.22, 1, 0.36, 1),
          backdrop-filter 280ms cubic-bezier(0.22, 1, 0.36, 1),
          -webkit-backdrop-filter 280ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      @media (max-width: 760px) {
        .header.home-video-cursor:not(.mobile-menu-surface) {
          --site-default-cursor: var(--site-white-default-cursor);
          --site-interactive-cursor: var(--site-white-interactive-cursor);
          cursor: var(--site-default-cursor);
        }
        :global(body:not(.home-mobile-hero-past) .header.home-route:not(.mobile-menu-surface)) {
          background: rgba(12, 12, 14, 0.18);
          backdrop-filter: blur(12px) saturate(135%);
          -webkit-backdrop-filter: blur(12px) saturate(135%);
        }
        :global(body:not(.home-mobile-hero-past) .header.home-route .mobile-global-nav),
        :global(body.dark-mode:not(.home-mobile-hero-past) .header.home-route .mobile-global-nav) {
          color: #f5f5f7;
          transition: color 240ms cubic-bezier(0.22, 1, 0.36, 1);
        }
      }
      .header.mobile-menu-surface,
      :global(body.dark-mode .header.mobile-menu-surface) {
        background: transparent;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
      }
      .mobile-global-nav.is-open .mobile-menu-toggle {
        color: #1d1d1f;
      }
      :global(body.dark-mode .mobile-global-nav.is-open .mobile-menu-toggle) {
        color: #f5f5f7;
      }
      .desktop-global-nav {
        display: none;
      }
      .mobile-global-nav {
        position: relative;
        z-index: 2;
        display: grid;
        grid-template-columns: minmax(2.75rem, 1fr) auto auto;
        align-items: center;
        box-sizing: border-box;
        width: min(var(--site-content-width), 90vw);
        margin: 0 auto;
        min-height: 48px;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
      }
      :global(body.dark-mode .mobile-global-nav) {
        color: #f5f5f7;
      }
      .mobile-global-identity {
        position: relative;
        z-index: 2;
        justify-self: start;
        color: #1d1d1f;
        font-size: 0.95rem;
        font-weight: 600;
        line-height: 1;
        letter-spacing: -0.025em;
        text-decoration: none;
        transition:
          color var(--theme-duration) ease,
          opacity 160ms ease,
          transform 130ms ease;
      }
      :global(body.dark-mode .mobile-global-identity) {
        color: #f5f5f7;
      }
      @media (max-width: 760px) {
        :global(body:not(.home-mobile-hero-past) .header.home-route .mobile-global-identity),
        :global(body.dark-mode:not(.home-mobile-hero-past) .header.home-route .mobile-global-identity) {
          color: #f5f5f7;
        }
      }
      .mobile-global-identity:active {
        transform: scale(0.95);
      }
      .mobile-global-actions {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        transition: opacity 150ms ease, transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .mobile-global-control,
      .mobile-menu-toggle {
        display: grid;
        width: 36px;
        height: 44px;
        place-items: center;
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .mobile-global-control {
        font-size: 0.82rem;
        line-height: 1;
        transition: opacity 120ms ease, transform 120ms ease;
      }
      .mobile-global-control:active {
        transform: scale(0.9);
      }
      .mobile-global-control:focus-visible,
      .mobile-menu-toggle:focus-visible,
      .mobile-global-identity:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: -5px;
      }
      .mobile-menu-toggle {
        position: relative;
        z-index: 4;
        justify-self: end;
        opacity: 0.8;
        transition: opacity 320ms cubic-bezier(0.4, 0, 0.6, 1), color 320ms cubic-bezier(0.4, 0, 0.6, 1);
      }
      .mobile-menu-toggle:hover,
      .mobile-menu-toggle:focus-visible {
        opacity: 1;
      }
      .mobile-menu-toggle:active {
        opacity: 0.55;
      }
      .mobile-menu-icon {
        display: block;
        overflow: visible;
      }
      .mobile-global-nav.is-open .mobile-global-identity,
      .mobile-global-nav.is-open .mobile-global-actions {
        opacity: 0;
        pointer-events: none;
      }
      .mobile-menu-panel {
        position: fixed;
        inset: 0;
        z-index: 8999;
        min-height: 100dvh;
        overflow-y: auto;
        overscroll-behavior: contain;
        background: #fafafc;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif;
        transform: translateY(0);
      }
      .mobile-menu-panel:focus {
        outline: none;
      }
      :global(body.dark-mode .mobile-menu-panel) {
        background: #161617;
        color: #f5f5f7;
      }
      .mobile-menu-links {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.08rem;
        padding: 3.55rem clamp(3rem, 8vw, 6rem) 4rem;
      }
      .mobile-menu-links a {
        color: #333336;
        font-size: clamp(1.75rem, 7.4vw, 2.1rem);
        font-weight: 600;
        line-height: 1.38;
        letter-spacing: -0.035em;
        text-decoration: none;
        opacity: 1;
        transform: translateY(0);
        transition:
          color 320ms cubic-bezier(0.4, 0, 0.6, 1),
          opacity 240ms cubic-bezier(0.4, 0, 0.6, 1),
          transform 240ms cubic-bezier(0.4, 0, 0.6, 1);
        transition-delay:
          0ms,
          calc(80ms + var(--mobile-menu-index) * 20ms),
          calc(80ms + var(--mobile-menu-index) * 20ms);
      }
      .mobile-menu-links a:hover,
      .mobile-menu-links a:focus-visible {
        color: #000;
      }
      .mobile-menu-links a:active {
        opacity: 0.48;
        transform: scale(0.985);
      }
      .mobile-menu-links a:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 4px;
      }
      .mobile-menu-links a.router-link-active {
        color: #6e6e73;
      }
      :global(body.dark-mode .mobile-menu-links a.router-link-active) {
        color: #a1a1a6;
      }
      :global(body.dark-mode .mobile-menu-links a) {
        color: #e8e8ed;
      }
      :global(body.dark-mode .mobile-menu-links a:hover),
      :global(body.dark-mode .mobile-menu-links a:focus-visible) {
        color: #fff;
      }
      :global(body.dark-mode .mobile-menu-links a.router-link-active:hover),
      :global(body.dark-mode .mobile-menu-links a.router-link-active:focus-visible) {
        color: #a1a1a6;
      }
      .mobile-menu-panel {
        animation: mobile-menu-surface-in 360ms cubic-bezier(0.32, 0.72, 0, 1) both;
        filter: drop-shadow(0 12px 18px rgba(0, 0, 0, 0.08));
        will-change: clip-path, transform;
      }
      .mobile-menu-panel.is-closing {
        animation: mobile-menu-surface-out 320ms cubic-bezier(0.32, 0.72, 0, 1) both;
        pointer-events: none;
      }
      .mobile-menu-panel.is-closing .mobile-menu-links a {
        opacity: 0;
        transform: translateY(-8px);
        transition-delay:
          0ms,
          calc(var(--mobile-menu-reverse-index) * 12ms),
          calc(var(--mobile-menu-reverse-index) * 12ms);
      }
      @media (prefers-reduced-motion: reduce) {
        .mobile-global-identity,
        .mobile-global-actions,
        .mobile-global-control,
        .mobile-menu-toggle,
        .mobile-menu-panel {
          transition-duration: 80ms;
          animation-duration: 80ms;
        }
        .mobile-menu-links a {
          transition-duration: 80ms;
          transition-delay: 0ms;
          opacity: 1;
          transform: none;
        }
      }
    }
    @keyframes mobile-menu-surface-in {
      from {
        clip-path: inset(0 0 100% 0);
        transform: translateY(-10px);
      }
      to {
        clip-path: inset(0);
        transform: translateY(0);
      }
    }
    @keyframes mobile-menu-surface-out {
      from {
        clip-path: inset(0);
        transform: translateY(0);
      }
      to {
        clip-path: inset(0 0 100% 0);
        transform: translateY(-10px);
      }
    }
    @media (min-width: 761px) and (max-width: 1100px) {
      .container::before {
        height: calc(56px + 0.82rem);
      }
      .mobile-global-nav {
        box-sizing: border-box;
        width: calc(100vw - clamp(80px, 12vw, 160px));
        min-height: 56px;
        padding-block: 0;
      }
      .mobile-global-identity {
        font-size: clamp(1.15rem, 2.4vw, 1.5rem);
        letter-spacing: -0.03em;
      }
      .mobile-global-control,
      .mobile-menu-toggle {
        width: clamp(42px, 5vw, 52px);
        height: clamp(44px, 5.2vw, 50px);
      }
      .mobile-global-control {
        font-size: clamp(0.95rem, 1.5vw, 1.15rem);
      }
      .mobile-menu-icon {
        transform: translateX(14px) scale(1.2);
      }
      .mobile-menu-links {
        padding-top: 5.25rem;
        padding-inline: clamp(5rem, 12vw, 9rem);
      }
    }
    @media (max-width: 350px) {
      .mobile-global-control {
        width: 33px;
      }
      .mobile-menu-toggle {
        width: 34px;
      }
      .mobile-menu-links {
        padding-inline: 2.25rem;
      }
    }
    @media (min-width: 840px) {
      .desktop-global-nav .site-control {
        transition: color 100ms ease, background-color 100ms ease, scale 100ms var(--ease-out);
      }
    }
    @media (min-width: 1101px) {
      .desktop-global-nav .social-icons .site-control {
        font-size: 1.66rem;
      }
    }
    /* Hairline reading-progress indicator at the very top of the viewport */
    .scroll-progress {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
      transform: scaleX(0);
      transform-origin: left center;
      background: #333;
      z-index: 9999;
      pointer-events: none;
      transition: transform 0.08s linear;
      view-transition-name: site-scroll-progress;
    }
    body.dark-mode .scroll-progress {
      background: #d8d8d8;
    }
    html.theme-transitioning .scroll-progress,
    body.theme-transitioning .scroll-progress {
      transition:
        transform 0.08s linear,
        background-color var(--theme-duration, 220ms) ease;
    }
    @media (min-width: 840px) and (min-height: 620px) and (hover: hover) and (pointer: fine) {
      .margin-dock-active {
        --margin-dock-edge: clamp(18px, 2.4vw, 30px);
      }

      .margin-dock-home {
        position: fixed;
        z-index: 85;
        top: 28px;
        left: var(--margin-dock-edge);
        display: block;
        width: 95px;
        text-decoration: none;
        user-select: none;
      }
      .margin-dock-home:focus-visible { outline: 1px solid currentColor; outline-offset: 6px; border-radius: 2px; }
      .margin-dock-identity {
        --identity-height: 28px;
        display: block;
        color: #424242;
        font-family: "Quattrocento Sans", sans-serif;
        font-size: 1.62rem;
        line-height: 1;
        letter-spacing: -0.025em;
        text-decoration: none;
        opacity: 0.82;
        transition: color 160ms ease;
      }
      .margin-dock-home:hover .margin-dock-identity {
        color: #171717;
      }

      .margin-dock-subtitle {
        display: block;
        width: 94px;
        margin-top: 14px;
        margin-left: 1px;
        padding: 0;
        color: #8d8d8d;
        font-family: "Quattrocento Sans", sans-serif;
        font-size: 0.58rem;
        line-height: 1.35;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        opacity: 0.68;
        transition: color 160ms ease;
      }
      .margin-dock-home:hover .margin-dock-subtitle { color: #757575; }

      .margin-dock-page {
        position: fixed;
        z-index: 85;
        bottom: 30px;
        left: var(--margin-dock-edge);
        color: #c1beb9;
        pointer-events: none;
        user-select: none;
      }

      .margin-dock-controls {
        position: fixed;
        z-index: 85;
        top: 50%;
        right: var(--margin-dock-edge);
        display: flex;
        width: 58px;
        margin: 0;
        flex-direction: column;
        align-items: flex-end;
        gap: 0;
        transform: translateY(-50%);
      }

      .margin-dock-control-zone {
        --dock-control-size: 36px;
        display: flex;
        box-sizing: border-box;
        width: clamp(
          84px,
          calc((100vw - var(--site-content-width)) / 2 - var(--margin-dock-edge) - 16px),
          132px
        );
        height: 50px;
        padding-right: calc((58px - var(--dock-control-size)) / 2);
        flex: 0 0 50px;
        align-items: center;
        justify-content: flex-end;
      }

      .margin-dock-theme-zone {
        --dock-control-size: 34px;
      }

      .margin-dock-search-zone {
        --dock-control-size: 44px;
      }

      .margin-dock-controls .margin-dock-control {
        position: relative;
        flex: 0 0 auto;
        border: 0;
        background: #fff;
        color: #686868;
        font-size: 1.15rem;
        opacity: 0.62;
        transition:
          color 100ms ease,
          background-color var(--theme-duration, 220ms) ease,
          border-color var(--theme-duration, 220ms) ease,
          opacity 100ms ease,
          translate 260ms cubic-bezier(0.22, 1, 0.36, 1),
          scale 160ms cubic-bezier(0.23, 1, 0.32, 1);
      }

      .margin-dock-controls .margin-dock-search {
        width: 44px;
        height: 44px;
        border: 0;
        font-size: 1.28rem;
        opacity: 0.88;
        translate: -10px 0;
      }

      .margin-dock-controls .margin-dock-theme {
        width: 34px;
        height: 34px;
      }

      .margin-dock-controls .margin-dock-music {
        width: 36px;
        height: 36px;
      }

      .margin-dock-controls .margin-dock-control::after {
        content: attr(aria-label);
        position: absolute;
        top: 50%;
        right: calc(100% + 10px);
        width: max-content;
        color: #777;
        font-family: "Quattrocento Sans", sans-serif;
        font-size: 0.62rem;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        opacity: 0;
        pointer-events: none;
        transform: translate3d(5px, -50%, 0);
        transition:
          opacity 150ms ease,
          transform 190ms cubic-bezier(0.23, 1, 0.32, 1),
          color var(--theme-duration, 220ms) ease;
      }

      :global(body.dark-mode .margin-dock-identity) {
        color: #dedede;
      }
      :global(body.dark-mode .margin-dock-home:hover .margin-dock-identity) {
        color: #f3f0eb;
      }
      :global(body.dark-mode .margin-dock-home:hover .margin-dock-subtitle) { color: #b0ada8; }

      :global(body.dark-mode .margin-dock-page) {
        color: #5c5b57;
      }

      :global(body.dark-mode .margin-dock-subtitle) {
        color: #979797;
      }

      :global(body.dark-mode .margin-dock-controls .margin-dock-control) {
        color: #a7a7a7;
        background: var(--dark-bg, #1c1c1e);
      }

      :global(body.dark-mode .margin-dock-controls .margin-dock-control::after) {
        color: #8e8e8e;
      }

      @media (hover: hover) and (pointer: fine) {
        .margin-dock-controls:has(.margin-dock-theme-zone:hover) .margin-dock-search,
        .margin-dock-controls:has(.margin-dock-music-zone:hover) .margin-dock-search,
        .margin-dock-controls:has(.music-controls-open) .margin-dock-search,
        .margin-dock-controls:has(.margin-dock-theme:focus-visible) .margin-dock-search,
        .margin-dock-controls:has(.margin-dock-music:focus-visible) .margin-dock-search {
          translate: 0 0;
        }

        .margin-dock-control-zone:hover .margin-dock-control,
        .margin-dock-control.music-controls-open,
        .margin-dock-control:focus-visible {
          color: #292929;
          opacity: 1;
          translate: -10px 0;
        }

        :global(body.dark-mode .margin-dock-control-zone:hover .margin-dock-control),
        :global(body.dark-mode .margin-dock-control.music-controls-open),
        :global(body.dark-mode .margin-dock-control:focus-visible) {
          color: #dedede;
        }

        .margin-dock-control-zone:hover .margin-dock-control::after,
        .margin-dock-control.music-controls-open::after,
        .margin-dock-control:focus-visible::after {
          opacity: 1;
          transform: translate3d(0, -50%, 0);
        }
      }
    }
    @media (min-width: 840px) and (max-width: 1100px) and (min-height: 620px) and (hover: hover) and (pointer: fine) {
      .margin-dock-left,
      .margin-dock-controls {
        display: none !important;
      }
    }
    .content-container.cv-route {
      margin-bottom: 0;
    }
    .view-source-return-active :deep(.back-link) {
      display: none !important;
    }
    .detail-source-views {
      width: 100%;
      max-width: none;
      margin: 2.8rem 0 0;
    }
    .ai-ready-notice {
      position: fixed;
      right: max(1rem, env(safe-area-inset-right));
      bottom: max(1rem, env(safe-area-inset-bottom));
      z-index: 10020;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      width: min(24rem, calc(100vw - 2rem));
      color: #2f2c27;
      background: color-mix(in srgb, #f2ead9 88%, transparent);
      border: 1px solid rgba(126, 101, 57, 0.27);
      border-radius: 14px;
      box-shadow: 0 18px 44px rgba(35, 29, 20, 0.16), 0 2px 8px rgba(35, 29, 20, 0.08);
      backdrop-filter: blur(18px) saturate(0.82);
      -webkit-backdrop-filter: blur(18px) saturate(0.82);
      overflow: hidden;
    }
    .ai-compose-notice {
      width: min(26rem, calc(100vw - 2rem));
      max-width: calc(100vw - 2rem);
      border-radius: 12px;
      box-shadow: 0 12px 32px rgba(35, 29, 20, 0.13), 0 1px 5px rgba(35, 29, 20, 0.07);
    }
    @media (max-width: 760px) {
      .ai-ready-notice {
        right: 1rem;
        left: 1rem;
        width: auto;
        max-width: 24rem;
        margin-inline: auto;
      }
      .ai-compose-notice {
        width: auto;
        max-width: 26rem;
      }
    }
    .ai-ready-notice.is-museum-notice {
      position: absolute;
      left: auto;
      right: max(3.8%, env(safe-area-inset-right));
      bottom: max(84px, calc(72px + env(safe-area-inset-bottom)));
      width: min(25rem, calc(100vw - 36px));
      max-width: none;
      color: var(--ex-ink);
      background: color-mix(in srgb, var(--ex-paper) 92%, transparent);
      border-color: var(--ex-border);
      pointer-events: auto;
    }
    .is-museum-notice.ai-compose-notice { pointer-events: none; }
    .is-museum-notice .ai-ready-main { font-family: "Quattrocento Sans", sans-serif; }
    .is-museum-notice :deep(.compose-progress-compact) { width: 100%; }
    .is-museum-notice :deep(.compose-progress) { color: var(--ex-ink); }
    .is-museum-notice :deep(.compose-copy > p),
    .is-museum-notice :deep(.compose-heading > span) { color: var(--ex-muted); }
    @media(max-width:767px) and (orientation:portrait) {
      .ai-ready-notice.is-museum-notice { right: 14px; bottom: calc(82px + env(safe-area-inset-bottom)); width: calc(100vw - 28px); }
    }
    @media(max-width:1180px) and (max-height:600px) and (orientation:landscape) {
      .ai-ready-notice.is-museum-notice { right: 22px; bottom: 72px; width: min(25rem, 44vw); }
    }
    .ai-compose-notice-button {
      display: block;
      width: 100%;
      padding: 0;
      color: inherit;
      background: transparent;
      border: 0;
      text-align: left;
      cursor: pointer;
      transition: background-color 160ms ease;
    }
    .ai-compose-notice-button:hover {
      background: rgba(150, 117, 62, 0.055);
    }
    .ai-compose-notice-button:focus-visible {
      outline: 2px solid rgba(115, 88, 42, 0.58);
      outline-offset: -3px;
    }
    :global(body.dark-mode .ai-compose-notice-button:hover) {
      background: rgba(203, 181, 138, 0.055);
    }
    :global(body.dark-mode .ai-ready-notice) {
      color: #ded8cc;
      background: color-mix(in srgb, #302d28 92%, transparent);
      border-color: rgba(205, 180, 132, 0.22);
      box-shadow: 0 18px 44px rgba(0, 0, 0, 0.34), 0 2px 8px rgba(0, 0, 0, 0.2);
    }
    :global(body.dark-mode .ai-ready-mark) {
      color: #cbb58a;
      background: rgba(193, 160, 101, 0.14);
    }
    :global(body.dark-mode .ai-ready-copy span),
    :global(body.dark-mode .ai-ready-arrow),
    :global(body.dark-mode .ai-ready-dismiss) {
      color: rgba(222, 216, 204, 0.6);
    }
    .ai-ready-main {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      align-items: center;
      gap: 0.78rem;
      min-width: 0;
      padding: 0.9rem 0.75rem 0.9rem 1rem;
      color: inherit;
      background: transparent;
      border: 0;
      text-align: left;
      cursor: pointer;
    }
    .ai-ready-main:focus-visible,
    .ai-ready-dismiss:focus-visible {
      outline: 2px solid rgba(115, 88, 42, 0.58);
      outline-offset: -3px;
    }
    .ai-ready-mark {
      display: grid;
      place-items: center;
      width: 1.85rem;
      height: 1.85rem;
      border-radius: 50%;
      background: rgba(150, 117, 62, 0.13);
      color: #7c6033;
      font-size: 0.9rem;
    }
    .ai-ready-copy {
      display: grid;
      min-width: 0;
      gap: 0.15rem;
      font-family: "Courier Prime", monospace;
    }
    .ai-ready-copy strong {
      font-size: 0.87rem;
      font-weight: 600;
      letter-spacing: 0.01em;
    }
    .ai-ready-copy span {
      overflow: hidden;
      color: rgba(47, 44, 39, 0.68);
      font-size: 0.76rem;
      line-height: 1.35;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ai-ready-arrow {
      color: rgba(47, 44, 39, 0.62);
      transition: transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .ai-ready-main:hover .ai-ready-arrow {
      transform: translateX(3px);
    }
    .ai-ready-dismiss {
      align-self: stretch;
      min-width: 2.4rem;
      padding: 0 0.35rem;
      color: rgba(47, 44, 39, 0.46);
      background: transparent;
      border: 0;
      cursor: pointer;
      font: 1.15rem/1 system-ui, sans-serif;
    }
    .ai-ready-enter-active,
    .ai-ready-leave-active {
      transition: opacity 220ms ease, transform 280ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .ai-ready-enter-from,
    .ai-ready-leave-to {
      opacity: 0;
      transform: translateY(12px) scale(0.98);
    }
    @media (prefers-reduced-transparency: reduce) {
      .ai-ready-notice {
        background: #f2ead9;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .ai-ready-enter-active,
      .ai-ready-leave-active {
        transition: opacity 100ms ease;
      }
      .ai-ready-enter-from,
      .ai-ready-leave-to {
        transform: none;
      }
      .fade-slide-enter-active,
      .fade-slide-leave-active,
      .route-fade-enter-active,
      .route-fade-leave-active,
      .view-compose-enter-active,
      .view-compose-leave-active {
        transition: opacity 0.1s ease;
      }
      .fade-slide-enter-from,
      .fade-slide-leave-to {
        transform: none;
      }
      .view-compose-enter-from,
      .view-compose-leave-to {
        transform: none;
      }
      .margin-dock-identity,
      .margin-dock-identity:hover,
      .margin-dock-identity:active {
        transform: none;
        scale: none;
        transition: color 120ms ease, opacity 120ms ease;
      }
      .margin-dock-identity::after,
      .margin-dock-identity:hover::after {
        display: none;
      }
      .margin-dock-identity:hover {
        letter-spacing: -0.025em;
      }
      .margin-dock-controls .margin-dock-control {
        transition:
          color var(--theme-duration, 220ms) ease,
          background-color var(--theme-duration, 220ms) ease,
          border-color var(--theme-duration, 220ms) ease,
          opacity 120ms ease;
      }
    }
</style>
